"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  Check,
  Clapperboard,
  Copy,
  MonitorSmartphone,
  Link2,
  MoreHorizontal,
  Music,
  Plus,
  Share2,
  Shuffle,
  ThumbsDown,
} from "lucide-react";
import { toast } from "sonner";
import { usePlayerStore, useSiteAuthStore } from "@gerama/stores";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@gerama/ui";
import { env } from "@/env";
import { browserCoreApi } from "@/lib/api";
import { songToTrack } from "@/features/player/to-track";
import { useArtistFollow } from "./follow-artist-button";
import { BRAND, appUri } from "@/lib/brand";
import { openInApp } from "@/lib/open-in-app";
import { artistPath } from "./artist-path";
import { publicPath } from "@/lib/public-path";

/** Plays an artist's songs in random order (the biggest first 100), through the global player. */
export async function shuffleArtistSongs(artistId: number, artistName: string, artistCode?: string) {
  const { list } = await browserCoreApi.songs.list({ artistId, limit: 100, sort: "popular" });
  const playable = list.filter((song) => song.hlsPath);
  if (playable.length === 0) {
    toast.info("هنوز آهنگی برای پخش نیست");
    return;
  }
  const store = usePlayerStore.getState();
  usePlayerStore.setState({ shuffle: true });
  const tracks = playable.map(songToTrack);
  store.playQueue(tracks, Math.floor(Math.random() * tracks.length), {
    label: artistName,
    href: artistPath({ id: artistId, code: artistCode }),
  });
}

/** Opens a random video of the artist. */
export async function shuffleArtistVideos(artistId: number, push: (href: string) => void) {
  const { list } = await browserCoreApi.videos.list({ artistId, limit: 50, sort: "popular" });
  if (list.length === 0) {
    toast.info("هنوز نماهنگی نیست");
    return;
  }
  push(publicPath("videos", list[Math.floor(Math.random() * list.length)]!));
}

/** The "…" menu of the artist page: shuffle, follow, suggest less, desktop app and share. */
export function ArtistMenu({
  artistId,
  artistCode,
  artistName,
  suggestLess,
}: {
  artistId: number;
  artistCode?: string | null;
  artistName: string;
  suggestLess: boolean;
}) {
  const router = useRouter();
  const client = useQueryClient();
  const token = useSiteAuthStore((s) => s.token);
  const { following, toggle } = useArtistFollow(artistId);

  const less = useMutation({
    mutationFn: (on: boolean) => browserCoreApi.playback.setSuggestLess(artistId, on),
    onSuccess: (on) => {
      void client.invalidateQueries({ queryKey: ["artists", "overview", artistId] });
      // Recommendations everywhere depend on this list.
      void client.invalidateQueries({ queryKey: ["discover"] });
      toast.success(on ? "کمتر از این هنرمند پیشنهاد می‌دهیم" : "پیشنهادها به حالت قبل برگشت");
    },
    onError: () => toast.error("انجام نشد"),
  });

  const copy = (text: string, done: string) =>
    navigator.clipboard.writeText(text).then(
      () => toast.success(done),
      () => toast.error("کپی نشد"),
    );

  return (
    <DropdownMenu dir="rtl">
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="گزینه‌های بیشتر"
          className="text-muted-foreground hover:text-foreground flex size-10 items-center justify-center rounded-full"
        >
          <MoreHorizontal className="size-6" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Shuffle className="size-4" />
            پخش تصادفی
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onSelect={() => void shuffleArtistSongs(artistId, artistName, artistCode ?? undefined)}>
              <Music className="size-4" />
              آهنگ‌ها
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void shuffleArtistVideos(artistId, router.push)}>
              <Clapperboard className="size-4" />
              نماهنگ‌ها
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onSelect={toggle}>
          {following ? <Check className="size-4" /> : <Plus className="size-4" />}
          {following ? "لغو دنبال کردن" : "دنبال کردن"}
        </DropdownMenuItem>
        {token && (
          <DropdownMenuItem onSelect={() => less.mutate(!suggestLess)}>
            {suggestLess ? <Ban className="size-4" /> : <ThumbsDown className="size-4" />}
            {suggestLess ? "پیشنهاد به حالت عادی" : "کمتر پیشنهاد بده"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={() => openInApp(`artist/${artistId}`)}
        >
          <MonitorSmartphone className="size-4" />
          ادامه در اپلیکیشن
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Share2 className="size-4" />
            اشتراک‌گذاری
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              onSelect={() =>
                void copy(`${env.NEXT_PUBLIC_SITE_URL}${artistPath({ id: artistId, code: artistCode })}`, "لینک کپی شد")
              }
            >
              <Link2 className="size-4" />
              کپی لینک هنرمند
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => void copy(appUri(`artist/${artistId}`), `آدرس ${BRAND} کپی شد`)}
            >
              <Copy className="size-4" />
              کپی آدرس {BRAND}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
